import React, { useState, useEffect } from "react";

const MAKE_GIFT_ENDPOINT = "https://gift-makegift-336444799661.us-central1.run.app";


const GiftCheckout = ({ gift, couple, onClose, onSuccess }) => {
  const [step, setStep] = useState("datos");
  const [formData, setFormData] = useState({
    guestName: "",
    guestEmail: "",
    guestPhone: "",
    message: "",
    paymentMethod: "",
  });
  const [receipt, setReceipt] = useState(null); // File object
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptPreview, setReceiptPreview] = useState(null); // URL preview

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  // ✅ Nuevos estados para monto personalizado
  const [customAmount, setCustomAmount] = useState(gift?.price || 0);
  const [showCustomAmount, setShowCustomAmount] = useState(false);

  // ✅ Estados para tipo de cambio
  const [exchangeRate, setExchangeRate] = useState(null);
  const [loadingRate, setLoadingRate] = useState(true);

  const [copied, setCopied] = useState(null);
  const [toast, setToast] = useState(null);

  const [receiptUrl, setReceiptUrl] = useState(null);

  // ✅ Setear el primer método disponible automáticamente

  // Auto-hide toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // ✅ Detectar typos comunes en emails
  const detectEmailTypo = (email) => {
    if (!email || !email.includes('@')) return null;

    const commonTypos = {
      // Gmail
      'gmial.com': 'gmail.com',
      'gmal.com': 'gmail.com',
      'gamil.com': 'gmail.com',
      'gnail.com': 'gmail.com',
      'gmaill.com': 'gmail.com',
      'gmail.con': 'gmail.com',
      'gmail.co': 'gmail.com',
      'gmail.cm': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gmil.com': 'gmail.com',
      // Hotmail
      'hotmal.com': 'hotmail.com',
      'hotmial.com': 'hotmail.com',
      'hotmail.con': 'hotmail.com',
      'hotamil.com': 'hotmail.com',
      'hotmai.com': 'hotmail.com',
      'hotmil.com': 'hotmail.com',
      // Outlook
      'outlok.com': 'outlook.com',
      'outloo.com': 'outlook.com',
      'outlook.con': 'outlook.com',
      'outlool.com': 'outlook.com',
      // Yahoo
      'yahoo.con': 'yahoo.com',
      'yaho.com': 'yahoo.com',
      'yahooo.com': 'yahoo.com',
      'yhoo.com': 'yahoo.com',
      'yahoo.cm': 'yahoo.com',
      // Live
      'live.con': 'live.com',
      'liv.com': 'live.com',
      // iCloud
      'icloud.con': 'icloud.com',
      'iclod.com': 'icloud.com',
      'icoud.com': 'icloud.com',
    };

    const domain = email.split('@')[1]?.toLowerCase();
    return commonTypos[domain]
      ? { typo: domain, suggestion: commonTypos[domain] }
      : null;
  };

  // Usar en el componente
  const emailTypo = detectEmailTypo(formData.guestEmail);


  const copyToClipboard = async (text, key) => {
    if (!text) return;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      console.error("Clipboard error:", e);
    }
  };

  const preferredCurrency = couple?.preferences?.currency || "ARS";
  const accountInfoARS = couple?.accountInfoARS || {};
  const accountInfoUSD = couple?.accountInfoUSD || {};

  // ✅ Calcular progreso y restante
  const progress = ((gift?.amountReceived || 0) / gift?.price) * 100;
  const remaining = Math.max(0, gift?.price - (gift?.amountReceived || 0));
  const hasContributions = (gift?.amountReceived || 0) > 0;

  // Fetch exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch("https://dolarapi.com/v1/dolares/oficial");
        const data = await response.json();
        setExchangeRate(data.venta);
      } catch (error) {
        console.error("Error fetching exchange rate:", error);
        setExchangeRate(null);
      } finally {
        setLoadingRate(false);
      }
    };

    fetchExchangeRate();
  }, []);

  // Inicializar customAmount cuando cambia el gift
  useEffect(() => {
    setCustomAmount(gift?.price || 0);
  }, [gift]);

  const convertToUSD = (arsAmount) => {
    if (!exchangeRate) return null;
    return arsAmount / exchangeRate;
  };

  const formatPrice = (price, showConversion = false) => {
    const ars = new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
    }).format(price || 0);

    if (showConversion && exchangeRate && !loadingRate) {
      const usdAmount = convertToUSD(price);
      const usd = new Intl.NumberFormat("es-AR", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
      }).format(usdAmount);

      return `${ars} (≈ ${usd})`;
    }

    return ars;
  };

  const closeAll = () => {
    // Cierra el checkout
    onClose?.();

    // Cierra el modal de detalle del regalo (si está abierto)
    window.dispatchEvent(new CustomEvent("closeGiftDetail"));
  };

  // ✅ Función para formatear teléfono
  const handlePhoneChange = (e) => {
    let value = e.target.value;

    // Limpiar: solo dígitos y +
    let cleaned = value.replace(/[^\d+]/g, '');

    // El + solo puede estar al inicio
    if (cleaned.includes('+') && !cleaned.startsWith('+')) {
      cleaned = '+' + cleaned.replace(/\+/g, '');
    }

    // Máximo un +
    const plusCount = (cleaned.match(/\+/g) || []).length;
    if (plusCount > 1) {
      cleaned = '+' + cleaned.replace(/\+/g, '');
    }

    setFormData(p => ({ ...p, guestPhone: cleaned }));
  };

  // ✅ Validar si el teléfono parece válido
  const phoneDigits = formData.guestPhone.replace(/\D/g, '').length;
  const hasPhoneInput = formData.guestPhone.length > 0;
  const isPhoneIncomplete = hasPhoneInput && phoneDigits > 0 && phoneDigits < 8;
  const isPhoneValid = phoneDigits >= 8;


  const canContinue =
    formData.guestName.trim().length > 1 &&
    formData.guestEmail.trim().length > 3 &&
    customAmount >= 100;

  // const paymentOptions = [
  //   { id: "transferencia", label: "Transferencia", icon: "🏦" },
  //   { id: "mercadopago", label: "Mercado Pago", icon: "💳" },
  //   { id: "efectivo", label: "Efectivo", icon: "💵" },
  //   { id: "paypal", label: "PayPal", icon: "🌐" },
  // ];
  // ✅ REEMPLAZAR CON:


  const paymentOptions = React.useMemo(() => {
    const methods = couple?.paymentMethods || {};
    const options = [];

    // Transferencia (mostrar solo si está habilitada Y configurada)
    const hasARSTransfer = methods.transferARS !== false &&
      (accountInfoARS?.alias || accountInfoARS?.cbu);
    const hasUSDTransfer = methods.transferUSD !== false &&
      (accountInfoUSD?.alias || accountInfoUSD?.cbu);

    if (hasARSTransfer || hasUSDTransfer) {
      options.push({ id: "transferencia", label: "Transferencia", icon: "🏦" });
    }

    // Mercado Pago
    if (methods.mercadoPago !== false &&
      (couple?.mercadoPago?.alias || couple?.mercadoPago?.cvu)) {
      options.push({ id: "mercadopago", label: "Mercado Pago", icon: "💳" });
    }

    // Efectivo
    if (methods.efectivo !== false) {
      options.push({ id: "efectivo", label: "Efectivo", icon: "💵" });
    }

    // PayPal
    if (methods.paypal !== false && accountInfoUSD?.paypalEmail) {
      options.push({ id: "paypal", label: "PayPal", icon: "🌐" });
    }

    return options;
  }, [couple, accountInfoARS, accountInfoUSD]);

  React.useEffect(() => {
    if (paymentOptions.length > 0 && !formData.paymentMethod) {
      setFormData(prev => ({
        ...prev,
        paymentMethod: paymentOptions[0].id
      }));
    }
  }, [paymentOptions, formData.paymentMethod]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };



  const handleReceiptChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setToast({
        title: "Formato no válido",
        message: "Solo se aceptan imágenes JPG, PNG o WEBP"
      });
      return;
    }

    // Validar tamaño (máx 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setToast({
        title: "Archivo muy grande",
        message: "El comprobante no puede superar 5MB"
      });
      return;
    }

    setReceipt(file);
    setReceiptUrl(null); // importante: resetea URL anterior

    // Crear preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setReceiptPreview(reader.result);
    };
    reader.readAsDataURL(file);
    // 🔥 subir en paralelo (no espera al Confirmar)
    const url = await uploadReceipt(file);
    if (url) setReceiptUrl(url);

  };

  const removeReceipt = () => {
    setReceipt(null);
    setReceiptPreview(null);
    setReceiptUrl(null);
  };

  // const uploadReceipt = async () => {
  //  if (!receipt) {
  //   // console.log('❌ No hay receipt para subir');
  //   return null;
  // }

  //   try {
  //     setUploadingReceipt(true);
  //     //  console.log('📤 Iniciando upload de comprobante:', {
  //     // name: receipt.name,
  //     // type: receipt.type,
  //     // size: receipt.size
  //   // });

  //     // 1️⃣ Pedir URL de subida pre-firmada
  //     const urlResponse = await fetch('https://gift-generateuploadurl-336444799661.us-central1.run.app', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({
  //         filename: receipt.name,
  //         contentType: receipt.type,
  //         coupleId: couple?.id,
  //         giftId: gift?.id,
  //         guestEmail: formData.guestEmail,
  //       }),
  //     });

  //     const urlData = await urlResponse.json();

  //     if (!urlData.success) {
  //       throw new Error(urlData.error || 'Error al generar URL de subida');
  //     }

  //     // 2️⃣ Subir archivo usando la URL pre-firmada
  //   // console.log('📤 Subiendo archivo a Storage...');
  //   // console.log('🔗 Upload URL:', urlData.uploadUrl.substring(0, 100) + '...');


  //     // 2️⃣ Subir archivo usando la URL pre-firmada
  //     const uploadResponse = await fetch(urlData.uploadUrl, {
  //       method: 'PUT',
  //       headers: {
  //         'Content-Type': receipt.type,
  //       },
  //       body: receipt,
  //     });

  //     if (!uploadResponse.ok) {
  //       throw new Error('Error al subir archivo a Storage');
  //     }

  //     // console.log('✅ Comprobante subido exitosamente:', urlData.publicUrl);

  //     return urlData.publicUrl;
  //   } catch (error) {
  //     console.error('❌ Error uploading receipt:', error);
  //     setToast({
  //       title: "Error al subir comprobante",
  //       message: "Podés continuar sin comprobante"
  //     });
  //     return null;
  //   } finally {
  //     setUploadingReceipt(false);
  //   }
  // };
  const uploadReceipt = async (file) => {
    if (!file) return null;

    try {
      setUploadingReceipt(true);

      const urlResponse = await fetch(
        "https://gift-generateuploadurl-336444799661.us-central1.run.app",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            filename: file.name,
            contentType: file.type,
            coupleId: couple?.id,
            giftId: gift?.id,
            guestEmail: formData.guestEmail,
          }),
        }
      );

      const urlData = await urlResponse.json();
      if (!urlData.success) {
        throw new Error(urlData.error || "Error al generar URL de subida");
      }

      const uploadResponse = await fetch(urlData.uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error("Error al subir archivo a Storage");
      }

      return urlData.publicUrl;
    } catch (error) {
      console.error("❌ Error uploading receipt:", error);
      setToast({
        title: "Error al subir comprobante",
        message: "Intentá de nuevo o usá otra imagen",
      });
      return null;
    } finally {
      setUploadingReceipt(false);
    }
  };


  const PaymentInstructions = () => {
    const methods = couple?.paymentMethods || {};

    // TRANSFERENCIA
    if (formData.paymentMethod === "transferencia") {
      const showARS =
        methods.transferARS !== false &&
        (accountInfoARS?.alias || accountInfoARS?.cbu);
      const showUSD =
        methods.transferUSD !== false &&
        (accountInfoUSD?.alias || accountInfoUSD?.cbu);

      if (!showARS && !showUSD) {
        return (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              No hay cuentas bancarias habilitadas para esta lista.
            </p>
          </div>
        );
      }

      return (
        <div className="space-y-3">
          {showARS && (
            <div className="rounded-2xl border border-accent-200 bg-accent-50 p-4">
              <p className="font-semibold text-neutral-900 mb-2">
                💵 Cuenta en Pesos (ARS)
              </p>
              <div className="text-sm text-neutral-700 space-y-1">
                {accountInfoARS?.alias && (
                  <div className="flex items-center justify-between gap-3">
                    <p>
                      <span className="font-medium">Alias:</span>{" "}
                      <span className="font-mono font-semibold">
                        {accountInfoARS.alias}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(accountInfoARS.alias, "ars-alias")
                      }
                      className="text-xs px-3 py-1.5 rounded-lg bg-white border border-accent-200 hover:bg-accent-100"
                    >
                      {copied === "ars-alias" ? "✅ Copiado" : "Copiar Alias"}
                    </button>
                  </div>
                )}

                {accountInfoARS?.cbu && (
                  <div className="flex items-center justify-between gap-3">
                    <p>
                      <span className="font-medium">CBU:</span>{" "}
                      <span className="font-mono font-semibold">
                        {accountInfoARS.cbu}
                      </span>
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        copyToClipboard(accountInfoARS.cbu, "ars-cbu")
                      }
                      className="text-xs px-3 py-1.5 rounded-lg bg-white border border-accent-200 hover:bg-accent-100"
                    >
                      {copied === "ars-cbu" ? "✅ Copiado" : "Copiar CBU"}
                    </button>
                  </div>
                )}

                {accountInfoARS?.accountHolder && (
                  <p>
                    <span className="font-medium">Titular:</span>{" "}
                    <span className="font-semibold">
                      {accountInfoARS.accountHolder}
                    </span>
                  </p>
                )}

                {accountInfoARS?.bank && (
                  <p>
                    <span className="font-medium">Banco:</span>{" "}
                    <span className="font-semibold">{accountInfoARS.bank}</span>
                  </p>
                )}

                <div className="pt-2 mt-2 border-t border-accent-200 flex items-center justify-between">
                  <span className="text-neutral-600">Tu aporte:</span>
                  <span className="font-bold text-lg text-accent-700">
                    {formatPrice(customAmount)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {showUSD && (
            <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
              <p className="font-semibold text-neutral-900 mb-2">
                💵 Cuenta en Dólares (USD)
              </p>
              <div className="text-sm text-neutral-700 space-y-1">
                {accountInfoUSD?.alias && (
                  <p>
                    <span className="font-medium">Alias:</span>{" "}
                    <span className="font-mono font-semibold">
                      {accountInfoUSD.alias}
                    </span>
                  </p>
                )}

                {accountInfoUSD?.cbu && (
                  <p>
                    <span className="font-medium">CBU:</span>{" "}
                    <span className="font-mono font-semibold">
                      {accountInfoUSD.cbu}
                    </span>
                  </p>
                )}

                {accountInfoUSD?.accountHolder && (
                  <p>
                    <span className="font-medium">Titular:</span>{" "}
                    <span className="font-semibold">
                      {accountInfoUSD.accountHolder}
                    </span>
                  </p>
                )}

                {accountInfoUSD?.bank && (
                  <p>
                    <span className="font-medium">Banco:</span>{" "}
                    <span className="font-semibold">{accountInfoUSD.bank}</span>
                  </p>
                )}

                <div className="pt-2 mt-2 border-t border-green-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-neutral-600">En ARS:</span>
                    <span className="font-semibold text-green-700">
                      {formatPrice(customAmount)}
                    </span>
                  </div>

                  {exchangeRate && !loadingRate ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-neutral-600">
                          Equivalente USD:
                        </span>
                        <span className="font-bold text-lg text-green-700">
                          {new Intl.NumberFormat("es-AR", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                          }).format(convertToUSD(customAmount))}
                        </span>
                      </div>
                      <p className="text-xs text-green-600 mt-2 italic">
                        * TC oficial: ${exchangeRate.toFixed(2)}
                      </p>
                    </>
                  ) : loadingRate ? (
                    <p className="text-xs text-neutral-500 italic">
                      Cargando TC...
                    </p>
                  ) : (
                    <p className="text-xs text-amber-600 italic">
                      No se pudo obtener el TC
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    // MERCADO PAGO
    if (formData.paymentMethod === "mercadopago") {
      const mpAlias = couple?.mercadoPago?.alias || "";
      const mpCVU = couple?.mercadoPago?.cvu || "";

      if (mpAlias || mpCVU) {
        return (
          <div className="space-y-3">
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
              <p className="font-semibold text-neutral-900 mb-2">
                Pagar con Mercado Pago
              </p>
              <div className="text-sm text-neutral-700 space-y-1">
                {mpAlias && (
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm text-neutral-700">
                      <span className="font-medium">Alias:</span>{" "}
                      <span className="font-mono font-semibold text-blue-700">
                        {mpAlias}
                      </span>
                    </p>

                    <button
                      type="button"
                      onClick={() => copyToClipboard(mpAlias, "mp-alias")}
                      className="shrink-0 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold hover:bg-blue-50"
                    >
                      {copied === "mp-alias" ? "✅ Copiado" : "Copiar Alias"}
                    </button>
                  </div>
                )}

                {mpCVU && (
                  <div className="flex items-center justify-between gap-3">
                    <p>
                      <span className="font-medium">CVU:</span>{" "}
                      <span className="font-mono font-semibold text-blue-700">
                        {mpCVU}
                      </span>
                    </p>

                    <button
                      type="button"
                      onClick={() => copyToClipboard(mpCVU, "mp-cvu")}
                      className="shrink-0 rounded-lg border border-blue-200 bg-white px-3 py-2 text-xs font-semibold hover:bg-blue-50"
                    >
                      {copied === "mp-cvu" ? "✅ Copiado" : "Copiar CVU"}
                    </button>
                  </div>
                )}

                <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between">
                  <span className="text-neutral-600">Tu aporte:</span>
                  <span className="font-bold text-blue-900">
                    {formatPrice(customAmount)}
                  </span>
                </div>
              </div>

              <p className="text-xs text-blue-800 mt-3">
                📱 Abrí la app de Mercado Pago y transferí el monto.
              </p>
            </div>

            {mpAlias && (
              <a
                href={`mercadopago://pay?alias=${encodeURIComponent(
                  mpAlias
                )}&amount=${customAmount}`}
                className="block w-full bg-blue-500 text-white text-center py-3 rounded-xl font-semibold hover:bg-blue-600 transition-colors md:hidden"
              >
                📱 Abrir en Mercado Pago
              </a>
            )}
          </div>
        );
      }

      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            Falta configurar datos de Mercado Pago.
          </p>
        </div>
      );
    }

    // EFECTIVO
    if (formData.paymentMethod === "efectivo") {
      return (
        <div className="rounded-2xl border border-green-200 bg-green-50 p-4">
          <p className="font-semibold text-neutral-900 mb-2">Pago en efectivo</p>
          <p className="text-sm text-green-800">
            Coordiná la entrega directamente con los novios.
          </p>
          <div className="pt-3 mt-3 border-t border-green-200 flex items-center justify-between">
            <span className="text-neutral-600">Tu aporte:</span>
            <span className="font-bold text-green-900">
              {formatPrice(customAmount)}
            </span>
          </div>
        </div>
      );
    }

    // PAYPAL
    if (formData.paymentMethod === "paypal") {
      const paypalEmail = accountInfoUSD?.paypalEmail || "";

      if (paypalEmail) {
        return (
          <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
            <p className="font-semibold text-neutral-900 mb-2">Pagar con PayPal</p>
            <div className="text-sm text-neutral-700 space-y-1">
              <p>
                <span className="font-medium">Enviar a:</span>{" "}
                <span className="font-mono font-semibold text-indigo-700">
                  {paypalEmail}
                </span>
              </p>
              <div className="pt-2 mt-2 border-t border-indigo-200 flex items-center justify-between">
                <span className="text-neutral-600">Tu aporte:</span>
                <span className="font-bold text-indigo-900">
                  {formatPrice(customAmount)}
                </span>
              </div>
            </div>
            <p className="text-xs text-indigo-800 mt-3">
              🌐 Abrí PayPal y enviá el pago.
            </p>
          </div>
        );
      }

      return (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-sm text-amber-900">
            Falta configurar el email de PayPal.
          </p>
        </div>
      );
    }

    return null;
  };
  const requiresReceipt = formData.paymentMethod !== "efectivo";
  // const hasReceipt = !!receipt; 
  const hasReceipt = !!receiptUrl; // ya subido

  const canConfirm =
    isPaymentMethodAvailable() &&
    !loading &&
    !uploadingReceipt &&
    (!requiresReceipt || hasReceipt);

  //   const handleSubmit = async () => {
  //     setApiError("");
  //     setLoading(true);


  // const requiresReceipt = formData.paymentMethod !== "efectivo";

  // if (requiresReceipt && !receiptUrl) {
  //   setToast({
  //     title: "Subiendo comprobante…",
  //     message: "Esperá un momento a que termine la carga.",
  //   });
  //   setLoading(false);
  //   return;
  // }

  //     try {
  //       // ✅ Subir comprobante si existe
  //     //    console.log('🎁 Iniciando handleSubmit');
  //     // console.log('📎 Receipt existe?', !!receipt);

  //      // ✅ Subir comprobante (obligatorio si no es efectivo)
  // let receiptUrl = null;

  // if (formData.paymentMethod !== "efectivo") {
  //   // 1) validación previa
  //   if (!receipt) {
  //     setToast({
  //       title: "Falta el comprobante",
  //       message: "Subí el comprobante para confirmar el regalo.",
  //     });
  //     setLoading(false);
  //     return;
  //   }

  //   // 2) upload
  //   receiptUrl = await uploadReceipt();

  //   // 3) si falló, cortar acá
  //   if (!receiptUrl) {
  //     setToast({
  //       title: "No se pudo subir el comprobante",
  //       message: "Intentá de nuevo o usá otra imagen.",
  //     });
  //     setLoading(false);
  //     return;
  //   }
  // }

  //     // console.log('📨 Enviando a giftMakeGift con receiptUrl:', receiptUrl);


  //       const res = await fetch(MAKE_GIFT_ENDPOINT, {
  //         method: "POST",
  //         headers: { "Content-Type": "application/json" },
  //         body: JSON.stringify({
  //           coupleId: couple?.id,
  //           giftId: gift?.id,
  //           guestName: formData.guestName,
  //           guestEmail: formData.guestEmail,
  //           guestPhone: formData.guestPhone,
  //           message: formData.message,
  //           paymentMethod: formData.paymentMethod,
  //           amount: customAmount,
  //           receiptUrl: receiptUrl, // ✅ Agregar URL del comprobante

  //         }),
  //       });

  //       const data = await res.json();

  //       if (!data?.success) {
  //         throw new Error(data?.error || "Error desconocido");
  //       }

  //       setStep("ok");
  //       onSuccess?.();
  //     } catch (err) {
  //       console.error("Error making gift:", err);
  //       setApiError(
  //         `❌ Error: ${err.message || "No se pudo procesar el regalo"}`
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  const handleSubmit = async () => {
    setApiError("");
    setLoading(true);

    const requiresReceipt = formData.paymentMethod !== "efectivo";

    // ✅ En A+B: el comprobante válido es el receiptUrl (ya subido)
    if (requiresReceipt && !receiptUrl) {
      setToast({
        title: "Subiendo comprobante…",
        message: "Esperá un momento a que termine la carga.",
      });
      setLoading(false);
      return;
    }

    try {
      const res = await fetch(MAKE_GIFT_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          coupleId: couple?.id,
          giftId: gift?.id,
          guestName: formData.guestName,
          guestEmail: formData.guestEmail,
          guestPhone: formData.guestPhone,
          message: formData.message,
          paymentMethod: formData.paymentMethod,
          amount: customAmount,
          receiptUrl: receiptUrl || null, // ✅ usa el STATE
        }),
      });

      const data = await res.json();

      if (!data?.success) {
        throw new Error(data?.error || "Error desconocido");
      }

      setStep("ok");
      onSuccess?.();
    } catch (err) {
      console.error("Error making gift:", err);
      setApiError(`❌ Error: ${err.message || "No se pudo procesar el regalo"}`);
    } finally {
      setLoading(false);
    }
  };


  // ✅ Mover el useMemo ANTES del if
  const relatedGifts = React.useMemo(() => {
    if (!gift?.category || step !== "ok") return [];

    return (couple?.gifts || [])
      .filter(g =>
        g.id !== gift.id &&
        g.category === gift.category &&
        g.active &&
        !g.reserved
      )
      .slice(0, 3);
  }, [gift, couple, step]);



  // // ✅ DEBUG: Ver qué métodos están disponibles
  // console.log('🔍 DEBUG paymentOptions:', {
  //   paymentOptions,
  //   methods: couple?.paymentMethods,
  //   mercadoPago: couple?.mercadoPago,
  //   hasAlias: !!couple?.mercadoPago?.alias,
  //   hasCVU: !!couple?.mercadoPago?.cvu,
  // });

  // OK screen
  if (step === "ok") {
    return (
      <div className="fixed inset-0 z-50 bg-neutral-900/30 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeAll();
          }
        }}>
        <div
          className="w-full max-w-2xl rounded-3xl bg-white overflow-hidden max-h-[90vh] flex flex-col ring-1 ring-black/5 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.35)]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header con animación */}
          <div className="bg-gradient-to-r from-rose-400 to-pink-500 px-8 pt-8 pb-12 sm:pb-16 text-center relative overflow-visible">            <button
            onClick={closeAll}
            className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center text-2xl transition-colors"
            aria-label="Cerrar"
          >
            ×
          </button>

            {/* fondo decorativo atrás */}
            <div className="absolute inset-0 opacity-20 pointer-events-none z-0">
              <div className="absolute top-0 left-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: "0ms" }} />
              <div className="absolute top-10 right-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: "200ms" }} />
              <div className="absolute bottom-10 left-1/3 w-2 h-2 bg-white rounded-full animate-ping" style={{ animationDelay: "400ms" }} />
            </div>

            {/* contenido adelante */}
            <div className="relative z-10">
              <div className="text-6xl mb-3 animate-bounce">🎉</div>

              <h3 className="text-3xl font-display font-bold text-white mb-2">
                ¡Gracias por tu regalo!
              </h3>

              <p className="text-white/90 text-lg">
                <span className="font-semibold">{formData?.guestName || "¡Genial!"}</span>, tu aporte fue registrado exitosamente
              </p>
            </div>
          </div>

          {/* Separación premium */}
          <div className="relative -mt-6">
            <div className="h-6 bg-white rounded-t-3xl shadow-sm" />
          </div>
          {/* Body */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Resumen */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-semibold text-green-900">Confirmación enviada</p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-600">Regalo:</span>
                  <span className="font-semibold text-neutral-900">{gift?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Tu aporte:</span>
                  <span className="font-bold text-green-700 text-lg">{formatPrice(customAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-600">Email:</span>
                  <span className="font-medium text-neutral-900">{formData.guestEmail}</span>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-green-200">
                <p className="text-xs text-green-800">
                  📧 Te enviamos un email de confirmación con todos los detalles del pago.
                </p>
              </div>
            </div>

            {/* Cross-sell */}
            {relatedGifts.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-display font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <span>💝</span>
                  Otros también eligieron estos regalos
                </h4>

                <div className="grid gap-3">
                  {relatedGifts.map(relatedGift => {
                    const relatedProgress = ((relatedGift.amountReceived || 0) / relatedGift.price) * 100;

                    return (
                      <button
                        key={relatedGift.id}
                        onClick={() => {
                          onClose?.();
                          setTimeout(() => {
                            const event = new CustomEvent('openGiftCheckout', { detail: relatedGift });
                            window.dispatchEvent(event);
                          }, 100);
                        }}
                        className="flex items-center gap-4 p-4 bg-white border border-neutral-200 rounded-xl hover:border-accent-300 hover:shadow-md transition-all text-left group"
                      >
                        {relatedGift.image && (
                          <img
                            src={relatedGift.image}
                            alt={relatedGift.name}
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                          />
                        )}

                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-neutral-900 group-hover:text-accent-600 transition-colors line-clamp-1">
                            {relatedGift.name}
                          </p>
                          <p className="text-sm text-neutral-600 mt-0.5">
                            {formatPrice(relatedGift.price)}
                          </p>

                          {relatedGift.amountReceived > 0 && relatedProgress < 100 && (
                            <div className="mt-2">
                              <div className="w-full h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-accent-500"
                                  style={{ width: `${Math.min(relatedProgress, 100)}%` }}
                                />
                              </div>
                              <p className="text-xs text-neutral-500 mt-1">
                                {Math.round(relatedProgress)}% completado
                              </p>
                            </div>
                          )}
                        </div>

                        <svg
                          className="w-5 h-5 text-neutral-400 group-hover:text-accent-600 transition-colors flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CTAs */}
            <div className="space-y-3">
              {/* <button
                onClick={() => {
                  onClose?.();
                  setTimeout(() => {
                    const catalog = document.querySelector('[data-catalog]');
                    if (catalog) {
                      catalog.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }, 100);
                }}
                className="w-full py-3 px-4 bg-accent-500 text-white rounded-xl font-semibold hover:bg-accent-600 transition-colors"
              >
                Ver más regalos
              </button> */}
              <button
                onClick={() => {
                  onClose?.(); // Cierra el checkout
                  // Cerrar también el modal de detalle si existe
                  const closeEvent = new CustomEvent('closeGiftDetail');
                  window.dispatchEvent(closeEvent);

                  setTimeout(() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }, 100);
                }}
                className="w-full py-3 px-4 bg-accent-500 text-white rounded-xl font-semibold hover:bg-accent-600 transition-colors"
              >
                Ver más regalos
              </button>
              <button
                type="button"
                onClick={closeAll}
                className="w-full py-3 px-4 bg-neutral-100 text-neutral-700 rounded-xl font-semibold hover:bg-neutral-200 transition-colors"
              >
                Cerrar
              </button>
            </div>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-500">
                💛 Gracias por ser parte de este momento especial
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  // ✅ Función para validar si el método de pago está disponible
  function isPaymentMethodAvailable() {
    const methods = couple?.paymentMethods || {};

    switch (formData.paymentMethod) {
      case "transferencia": {
        const hasARS =
          methods.transferARS !== false && (accountInfoARS?.alias || accountInfoARS?.cbu);
        const hasUSD =
          methods.transferUSD !== false && (accountInfoUSD?.alias || accountInfoUSD?.cbu);
        return hasARS || hasUSD;
      }

      case "mercadopago":
        return (
          methods.mercadoPago !== false &&
          (couple?.mercadoPago?.alias || couple?.mercadoPago?.cvu)
        );

      case "efectivo":
        return methods.efectivo !== false;

      case "paypal":
        return methods.paypal !== false && !!accountInfoUSD?.paypalEmail;

      default:
        return false;
    }
  }

  // Main checkout form
  return (
    <div
      className="fixed inset-0 z-50 bg-neutral-900/30 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4"
    // onClick={closeAll}
    >
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,520px)]">
          <div className="rounded-2xl bg-neutral-900/90 backdrop-blur text-white shadow-[0_18px_60px_-20px_rgba(0,0,0,0.6)] px-5 py-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold">{toast.title}</p>
              <p className="text-sm text-neutral-200 mt-1">{toast.message}</p>
            </div>

            <button
              type="button"
              onClick={() => setToast(null)}
              className="rounded-xl bg-white/10 hover:bg-white/20 px-3 py-1 text-sm font-semibold"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      <div
        className="w-full max-w-3xl rounded-3xl bg-white overflow-hidden max-h-[90vh] flex flex-col ring-1 ring-black/5 shadow-[0_24px_80px_-24px_rgba(0,0,0,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-neutral-200 flex-shrink-0">
          <div>
            <h3 className="text-lg sm:text-xl font-display font-bold text-neutral-900">
              {step === "datos" ? "Completar regalo" : "Información de pago"}
            </h3>
            <p className="text-xs sm:text-sm text-neutral-500">
              {gift?.name} ·{" "}
              <span className="font-semibold text-accent-600">
                {formatPrice(gift?.price, true)}
              </span>
            </p>
            {exchangeRate && (
              <p className="text-xs text-neutral-400 mt-0.5">
                TC oficial: ${exchangeRate.toFixed(2)}
              </p>
            )}
          </div>
          <button
            onClick={closeAll}
            className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 transition-colors flex items-center justify-center text-xl text-neutral-700 flex-shrink-0"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-5 sm:px-6 py-4 overflow-y-auto flex-1">
          {/* Progress */}
          <div className="flex items-center gap-2 mb-4">
            <div
              className={`h-1.5 flex-1 rounded-full ${step === "datos" ? "bg-accent-500" : "bg-accent-200"
                }`}
            />
            <div
              className={`h-1.5 flex-1 rounded-full ${step === "pago" ? "bg-accent-500" : "bg-neutral-200"
                }`}
            />
          </div>

          {apiError && (
            <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {apiError}
            </div>
          )}

          {step === "datos" ? (
            <div className="space-y-5">
              {/* MONTO PERSONALIZADO */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-display font-semibold text-neutral-900">
                    Monto del regalo
                  </h4>
                  {!showCustomAmount && (
                    <button
                      type="button"
                      onClick={() => setShowCustomAmount(true)}
                      className="text-sm text-accent-600 hover:text-accent-700 font-medium"
                    >
                      Personalizar →
                    </button>
                  )}
                </div>

                {hasContributions && (
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-neutral-600">Recaudado</span>
                      <span className="text-sm font-semibold text-neutral-900">
                        {formatPrice(gift.amountReceived)} de{" "}
                        {formatPrice(gift.price)}
                      </span>
                    </div>

                    <div className="w-full h-3 bg-neutral-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-accent-500 to-accent-600 transition-all duration-500"
                        style={{ width: `${Math.min(progress, 100)}%` }}
                      />
                    </div>

                    <p className="text-xs text-neutral-500 mt-2">
                      {progress >= 100 ? (
                        <span className="text-green-600 font-medium">
                          ✓ ¡Objetivo completado!
                        </span>
                      ) : (
                        <>
                          Faltan {formatPrice(remaining)} para completar (
                          {gift.contributors?.length || 0} aporte
                          {gift.contributors?.length === 1 ? "" : "s"})
                        </>
                      )}
                    </p>
                  </div>
                )}

                {showCustomAmount ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setCustomAmount(gift.price)}
                        className={`py-3 rounded-xl border font-medium transition-all ${customAmount === gift.price
                          ? "border-accent-500 bg-accent-50 text-accent-700"
                          : "border-neutral-200 hover:bg-neutral-50"
                          }`}
                      >
                        Completo
                      </button>
                      <button
                        type="button"
                        onClick={() => setCustomAmount(Math.ceil(gift.price / 2))}
                        className={`py-3 rounded-xl border font-medium transition-all ${customAmount === Math.ceil(gift.price / 2)
                          ? "border-accent-500 bg-accent-50 text-accent-700"
                          : "border-neutral-200 hover:bg-neutral-50"
                          }`}
                      >
                        Mitad
                      </button>
                      <button
                        type="button"
                        onClick={() => setCustomAmount(Math.ceil(gift.price / 4))}
                        className={`py-3 rounded-xl border font-medium transition-all ${customAmount === Math.ceil(gift.price / 4)
                          ? "border-accent-500 bg-accent-50 text-accent-700"
                          : "border-neutral-200 hover:bg-neutral-50"
                          }`}
                      >
                        Un cuarto
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        O elegí un monto personalizado
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-500 font-medium">
                          $
                        </span>
                        <input
                          type="number"
                          value={customAmount}
                          onChange={(e) =>
                            setCustomAmount(
                              Math.max(0, parseInt(e.target.value) || 0)
                            )
                          }
                          min="100"
                          step="100"
                          className="w-full pl-8 pr-4 py-3 rounded-xl border border-neutral-300 focus:outline-none focus:ring-4 focus:ring-accent-100 focus:border-accent-300 font-semibold text-lg"
                        />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-xs text-neutral-500">
                        <span>Mínimo: $100</span>
                        <span>Precio original: {formatPrice(gift.price)}</span>
                      </div>
                    </div>

                    {customAmount > gift.price && (
                      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
                        <p className="text-sm text-blue-800">
                          💝 ¡Qué generoso! Estás aportando{" "}
                          <strong>
                            {formatPrice(customAmount - gift.price)}
                          </strong>{" "}
                          extra
                        </p>
                      </div>
                    )}

                    {customAmount < gift.price && remaining > 0 && (
                      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                        <p className="text-sm text-amber-800">
                          Tu aporte cubre el{" "}
                          <strong>
                            {Math.round((customAmount / gift.price) * 100)}%
                          </strong>{" "}
                          del regalo.
                          {remaining - customAmount > 0 && (
                            <>
                              {" "}
                              Quedarán{" "}
                              {formatPrice(remaining - customAmount)} por
                              completar.
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4 text-center">
                    <p className="text-3xl font-bold text-accent-600">
                      {formatPrice(customAmount)}
                    </p>
                    <p className="text-sm text-neutral-600 mt-1">
                      {customAmount === gift.price ? "Precio completo" : "Tu aporte"}
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-neutral-200 my-5" />

              <div className="space-y-3">
                <h4 className="font-display font-semibold text-neutral-900">
                  Tus datos
                </h4>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    name="guestName"
                    value={formData.guestName}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 outline-none focus:border-neutral-500"
                    placeholder="Juan Pérez"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="guestEmail"
                      value={formData.guestEmail}
                      onChange={handleInputChange}
                      className={`w-full rounded-xl border px-4 py-2.5 outline-none focus:border-neutral-500 ${emailTypo ? 'border-amber-400 bg-amber-50' : 'border-neutral-300'
                        }`}
                      placeholder="tu@email.com"
                      required
                    />
                    {emailTypo ? (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <span>⚠️</span>
                        <span>
                          ¿Quisiste decir{' '}
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((p) => ({
                                ...p,
                                guestEmail: p.guestEmail.replace(/@.+$/, `@${emailTypo.suggestion}`),
                              }))
                            }
                            className="font-semibold underline hover:text-amber-700"
                          >
                            {formData.guestEmail.replace(/@.+$/, `@${emailTypo.suggestion}`)}
                          </button>
                          ?
                        </span>
                      </p>
                    ) : (
                      <p className="text-xs text-neutral-500 mt-1">
                        Te enviaremos la confirmación.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Teléfono (opcional)
                    </label>
                    <input
                      type="tel"
                      name="guestPhone"
                      value={formData.guestPhone}
                      onChange={handlePhoneChange}
                      className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 outline-none focus:border-neutral-500"
                      placeholder="+54 11 1234-5678"
                      maxLength={16}
                    />
                    {isPhoneIncomplete && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ El número parece incompleto
                      </p>
                    )}
                    {isPhoneValid && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Los novios podrán agradecerte por WhatsApp
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                    Dedicatoria (opcional)
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full rounded-xl border border-neutral-300 px-4 py-2.5 outline-none focus:border-neutral-500 resize-none"
                    placeholder="Escribí un mensaje para los novios..."
                  />
                </div>

                <div className="rounded-2xl border border-neutral-200 bg-neutral-50 p-4">
                  <p className="text-xs text-neutral-600">Resumen</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="font-medium text-neutral-900 line-clamp-1">
                      {gift?.name}
                    </p>
                    <p className="font-bold text-neutral-900">
                      {formatPrice(customAmount)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {paymentOptions.map((opt) => {
                  const selected = formData.paymentMethod === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() =>
                        setFormData((p) => ({ ...p, paymentMethod: opt.id }))
                      }
                      className={`rounded-2xl border px-4 py-3 text-left transition-all ${selected
                        ? "border-accent-500 bg-accent-50 shadow-sm"
                        : "border-neutral-200 hover:border-neutral-300 bg-white"
                        }`}
                    >
                      <div className="text-2xl">{opt.icon}</div>
                      <div className="mt-1 font-semibold text-neutral-900 text-sm">
                        {opt.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              <PaymentInstructions />

              {/* Comprobante opcional (solo para métodos que no son efectivo) */}
              {formData.paymentMethod !== "efectivo" && (
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="text-2xl">📎</div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-neutral-900 mb-1">
                        Comprobante de pago <span className="text-red-600">*</span>
                      </h4>
                      <p className="text-sm text-neutral-600">
                        Subir el comprobante ayuda a los novios a verificar tu regalo más rápido
                      </p>
                    </div>
                  </div>

                  {!receiptPreview ? (
                    <label className="block cursor-pointer">
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleReceiptChange}
                        className="hidden"
                      />
                      <div className="border-2 border-dashed border-blue-300 rounded-xl p-6 text-center hover:border-blue-400 hover:bg-blue-100/50 transition-colors">
                        <div className="text-3xl mb-2">📷</div>
                        <p className="text-sm font-medium text-blue-900">
                          Click para subir comprobante
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          JPG, PNG o WEBP - Máx 5MB
                        </p>
                      </div>
                    </label>
                  ) : (
                    <div className="relative">
                      <img
                        src={receiptPreview}
                        alt="Comprobante"
                        className="w-full h-48 object-contain rounded-xl border border-blue-200 bg-white"
                      />
                      <button
                        type="button"
                        onClick={removeReceipt}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                      <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Comprobante listo para subir
                      </p>
                    </div>
                  )}

                  {uploadingReceipt && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-blue-700">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700"></div>
                      Subiendo comprobante...
                    </div>
                  )}
                </div>
              )}

              <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                <p className="text-sm text-neutral-700">
                  <span className="font-semibold">Tus datos:</span>{" "}
                  {formData.guestName} · {formData.guestEmail}
                </p>
                {formData.message?.trim() && (
                  <p className="text-sm text-neutral-600 mt-1 line-clamp-2">
                    "{formData.message.trim()}"
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 sm:px-6 py-4 border-t border-neutral-200 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between flex-shrink-0">
          <button
            type="button"
            onClick={() => (step === "pago" ? setStep("datos") : closeAll())}
            className="w-full sm:w-auto rounded-2xl px-5 py-3 bg-neutral-100 text-neutral-800 font-semibold hover:bg-neutral-200 transition-colors"
            disabled={loading}
          >
            {step === "pago" ? "← Volver" : "Cancelar"}
          </button>

          {step === "datos" ? (
            <button
              type="button"
              onClick={() => {
                // ✅ Verificar que haya métodos antes de avanzar
                if (paymentOptions.length === 0) {
                  setToast({
                    title: "No hay métodos de pago",
                    message: "Por favor contactá a los novios para coordinar el pago."
                  });
                  return;
                }
                setStep("pago");
              }}
              className={`w-full sm:w-auto rounded-2xl px-6 py-3 font-semibold transition-colors ${canContinue && paymentOptions.length > 0
                ? "bg-accent-500 text-white hover:bg-accent-600"
                : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                }`}
              disabled={!canContinue || paymentOptions.length === 0}
            >
              Continuar →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              className={`w-full sm:w-auto rounded-2xl px-6 py-3 font-semibold transition-colors ${canConfirm
                ? "bg-accent-500 text-white hover:bg-accent-600"
                : "bg-neutral-200 text-neutral-500 cursor-not-allowed"
                }`}
              disabled={!canConfirm}
            >
              {uploadingReceipt
                ? "Subiendo comprobante..."
                : loading
                  ? "Procesando…"
                  : `Confirmar ${formatPrice(customAmount)}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCheckout;